import React, { useCallback, useEffect, useId, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { findProduct } from '../bz/products';
import { IcoCheck } from '../bz/icons';
import { ThresholdSlider, usePredictThreshold } from './PredictThreshold';
import BZAPIService from '../../services/BZAPIService';
import {
  formatScore,
  PROMOTION_SCORE,
  removedBy,
  thresholdKind,
  WATCH_LABEL,
  WATCH_PATH
} from './predictWatchlistModel';

const rule = { display: 'flex', gap: 10, color: 'var(--ink-soft)', fontSize: 'var(--fs-md)' };

const Rule = ({ children }) => (
  <li style={rule}>
    <IcoCheck size={14} style={{ flex: 'none', marginTop: 3, color: 'var(--predict)' }} />
    <span>{children}</span>
  </li>
);
Rule.propTypes = { children: PropTypes.node };

/**
 * Predict → Settings: the customer's score threshold, the lowest score that
 * reaches their watch list and outcomes (STRY0038971), as a slider with a large
 * readout (STRY0039087). It is org-wide. Everyone sees it; only an admin may
 * change it, and the API says which (`canEdit`, the same admin check its PUT
 * makes). The API also holds the range and step, so a value this page doesn't
 * offer is refused there too. This is the only page that changes it, and only
 * with the admin's approval: the slider and Reset set a pending value, shown as
 * "6.0 → 5.5, for everyone in your organization", which Apply saves and Cancel
 * drops. The watch list only shows it. Raising it also says how many bugs it
 * takes off the watch list, counted from GET /predict/watchlist; lowering it
 * would need bugs the list doesn't hold, so it says nothing more.
 *
 * Promotion and BugZero's own counts stay at 6.0 whatever the threshold is.
 */
const PredictSettings = () => {
  // The watch list at the saved threshold, for the what-if; reloaded after each Apply.
  const [watchlist, setWatchlist] = useState(null);
  const loadWatchlist = useCallback(() => {
    BZAPIService.getPredictWatchlist()
      .then(body => setWatchlist(Array.isArray(body?.bugs) ? { bugs: body.bugs, threshold: body.threshold } : null))
      .catch(error => {
        console.error('Failed to load the Predict watch list:', error);
        setWatchlist(null);
      });
  }, []);
  useEffect(() => {
    loadWatchlist();
  }, [loadWatchlist]);

  const threshold = usePredictThreshold({ onApplied: loadWatchlist });
  const sliderId = useId();
  const hintId = useId();
  const titleId = useId();
  const { setting, draft } = threshold;
  const kind = setting ? thresholdKind(draft, setting.default) : null;
  const removed =
    setting && watchlist && watchlist.threshold === setting.threshold
      ? removedBy(watchlist.bugs, setting.threshold, draft)
      : null;

  return (
    <div
      style={{
        padding: '24px 32px',
        maxWidth: 1320,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 20
      }}
    >
      <div className="bz-section-head" style={{ marginBottom: 0 }}>
        <div>
          <div className="bz-kicker" style={{ color: findProduct('predict').accent }}>
            Predict · Settings
          </div>
          <h1 className="bz-h1" style={{ marginTop: 4 }}>
            Settings
          </h1>
          <div className="bz-muted" style={{ fontSize: 'var(--fs-md)', marginTop: 4 }}>
            How Predict works for your organization.
          </div>
        </div>
      </div>

      <div className="bz-card" role="region" aria-labelledby={titleId}>
        <div className="bz-row" style={{ padding: '12px 16px', borderBottom: '1px solid var(--line-soft)', gap: 8 }}>
          <div className="bz-grow">
            <h2 id={titleId} className="bz-h3" style={{ margin: 0 }}>
              Score threshold
            </h2>
            <div className="bz-muted" style={{ fontSize: 'var(--fs-md)', marginTop: 4 }}>
              The lowest score that reaches your watch list and your outcomes. It applies to your whole organization.
            </div>
          </div>
          {setting && !setting.canEdit && <span className="bz-pill">View only</span>}
        </div>

        {threshold.status === 'loading' && !setting ? (
          <div className="text-center" style={{ padding: 32 }}>
            <Spinner color="primary" />
            <div className="bz-muted" style={{ marginTop: 8 }}>
              Loading the threshold…
            </div>
          </div>
        ) : threshold.status === 'failed' ? (
          <div className="bz-row" role="alert" style={{ padding: 16, gap: 12 }}>
            <span>The threshold can&apos;t be read right now.</span>
            <button type="button" className="bz-btn bz-btn--sm" onClick={threshold.reload}>
              Try again
            </button>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 28,
              padding: 20
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
              <div className="bz-row" style={{ gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div>
                  <label htmlFor={sliderId} className="bz-kicker" style={{ margin: 0 }}>
                    Your threshold
                  </label>
                  <div
                    className="bz-mono"
                    data-testid="threshold-readout"
                    style={{ fontSize: 40, fontWeight: 600, lineHeight: 1, letterSpacing: '-.02em' }}
                  >
                    {formatScore(draft)}
                  </div>
                </div>
                <span
                  className={`bz-pill${kind === 'Custom' ? ' bz-pill--predict' : ''}`}
                  data-testid="threshold-kind"
                  style={{ marginBottom: 4 }}
                >
                  {kind}
                </span>
              </div>
              <div style={{ width: 300, maxWidth: '100%' }}>
                <ThresholdSlider
                  id={sliderId}
                  value={draft}
                  range={setting}
                  disabled={!setting.canEdit}
                  onChange={threshold.change}
                  describedBy={hintId}
                  block
                />
                <div
                  className="bz-row bz-mono bz-muted"
                  style={{ justifyContent: 'space-between', fontSize: 'var(--fs-xs)' }}
                  aria-hidden="true"
                >
                  <span>{formatScore(setting.min)}</span>
                  <span>{formatScore(setting.max)}</span>
                </div>
              </div>
              {setting.canEdit ? (
                <>
                  {threshold.pending && (
                    <div className="pd-pending" role="status" data-testid="threshold-pending">
                      <span>
                        <span className="bz-mono" style={{ fontWeight: 600 }}>
                          {formatScore(setting.threshold)} &rarr; {formatScore(draft)}
                        </span>
                        {removed !== null && (
                          <span data-testid="threshold-removes">
                            {' '}
                            removes {removed} {removed === 1 ? 'bug' : 'bugs'} from your watch list
                          </span>
                        )}
                        , for everyone in your organization
                      </span>
                      <span className="bz-row" style={{ gap: 8 }}>
                        <button
                          type="button"
                          className="bz-btn bz-btn--sm bz-btn--primary"
                          onClick={threshold.apply}
                          disabled={threshold.saving}
                        >
                          {threshold.saving ? 'Applying…' : 'Apply'}
                        </button>
                        <button
                          type="button"
                          className="bz-btn bz-btn--sm"
                          onClick={threshold.cancel}
                          disabled={threshold.saving}
                        >
                          Cancel
                        </button>
                      </span>
                    </div>
                  )}
                  <div className="bz-row" style={{ gap: 8 }}>
                    <button
                      type="button"
                      className="bz-btn bz-btn--sm"
                      onClick={threshold.reset}
                      disabled={draft === setting.default || threshold.saving}
                    >
                      Reset to {formatScore(setting.default)}
                    </button>
                  </div>
                </>
              ) : (
                <div className="bz-muted" style={{ fontSize: 'var(--fs-sm)' }}>
                  Only an admin can change the threshold.
                </div>
              )}
            </div>

            <ul id={hintId} style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 10 }}>
              <Rule>
                Starts at {formatScore(setting.default)} and moves in steps of {setting.step}, from{' '}
                {formatScore(setting.min)} to {formatScore(setting.max)}.
              </Rule>
              <Rule>It changes what your organization sees. Other customers&apos; lists don&apos;t change.</Rule>
              <Rule>Below {formatScore(PROMOTION_SCORE)}, bugs reach your list marked &ldquo;Not promoted&rdquo;.</Rule>
              <Rule>
                Promotion and BugZero&apos;s counts stay at {formatScore(PROMOTION_SCORE)}, whatever you choose.
              </Rule>
            </ul>
          </div>
        )}
      </div>

      <div className="bz-card" role="region" aria-labelledby={`${titleId}-org`}>
        <div className="bz-row" style={{ padding: '12px 16px', borderBottom: '1px solid var(--line-soft)', gap: 8 }}>
          <div className="bz-grow">
            <h2 id={`${titleId}-org`} className="bz-h3" style={{ margin: 0 }}>
              Predict for your organization
            </h2>
            <div className="bz-muted" style={{ fontSize: 'var(--fs-md)', marginTop: 4 }}>
              BugZero turns Predict on and manages its coverage. Ask BugZero to change either.
            </div>
          </div>
          <span className="bz-pill bz-pill--predict">Managed by BugZero</span>
        </div>
        <div style={{ padding: '4px 16px' }}>
          <div className="pd-kv">
            <span>Predict</span>
            <span className="bz-row" style={{ gap: 8 }}>
              <span className="bz-pill bz-pill--ok">
                <span className="bz-dot" />
                On
              </span>
              <span className="bz-muted" style={{ fontSize: 'var(--fs-sm)' }}>
                Managed by BugZero
              </span>
            </span>
          </div>
          <div className="pd-kv">
            <span>Coverage</span>
            <span className="bz-row bz-muted" style={{ gap: 8 }}>
              Managed by BugZero, from your Prevent managed products
              <Link to="/predict/coverage" className="bz-btn bz-btn--ghost bz-btn--sm">
                View coverage
              </Link>
            </span>
          </div>
          <div className="pd-kv">
            <span>{WATCH_LABEL}</span>
            <span className="bz-row bz-muted" style={{ gap: 8 }}>
              {setting
                ? `Bugs scoring ${formatScore(setting.threshold)} or higher`
                : 'Bugs at your threshold or higher'}
              <Link to={WATCH_PATH} className="bz-btn bz-btn--ghost bz-btn--sm">
                See your watch list
              </Link>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictSettings;
